import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, ExternalLink } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import {
  buildContractPath,
  resolveContractLabel,
  type InstallationIdentityLike,
} from "@/lib/installationContext";
import { ContractBillingWorkspace } from "@/components/contracts/ContractBillingWorkspace";

// PR B — Installation bridge for contract-governed billing (single source).
//
// When an installation belongs to a contract, its billing is owned by the
// contract (mandate §5 / reuse map §2). Instead of duplicating a per-installation
// economic flow, this renders:
//   1. A banner "La facturación pertenece al contrato CTR-XXXX" + link.
//   2. The SHARED ContractBillingWorkspace (keyed by the resolved contractId).
//
// The installation "inherits" the contract economics; it never holds its own
// milestone/adjustment duplicates. Legacy installations (no contractId) keep the
// existing BillingTab flow untouched (handled by the caller).
export function ContractInstallationBilling({ installation }: { installation: InstallationIdentityLike }) {
  const [, setLocation] = useLocation();
  const contractRef = resolveContractLabel(installation);
  const contractId = installation.contractId != null ? Number(installation.contractId) : null;

  const resolveQuery = trpc.customers.contracts.economyByInstallation.useQuery(
    { installationId: installation.id },
    { enabled: contractId != null && contractId > 0 },
  );
  const resolvedContractId =
    (resolveQuery.data as { driver?: string; contractId?: number | null } | undefined)?.driver === "contract"
      ? (resolveQuery.data as { contractId?: number | null }).contractId ?? contractId
      : contractId;

  if (contractId == null || contractId <= 0) {
    // Handled upstream: legacy installations use BillingTab. Defensive no-op.
    return null;
  }

  return (
    <div className="space-y-4">
      <Card className="border-indigo-200 bg-indigo-50/50">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-indigo-600" />
            <span className="text-indigo-900">
              La facturación pertenece al contrato{" "}
              <Badge variant="outline" className="ml-1 font-mono align-middle">
                {contractRef ?? (resolvedContractId != null ? `CTR-${resolvedContractId}` : "")}
              </Badge>
            </span>
            <span className="text-muted-foreground text-xs">
              Esta instalación está vinculada a un contrato: su economía se gestiona a nivel de contrato (fuente única), no por instalación.
            </span>
          </div>
          {resolvedContractId != null && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const resolvedData = resolveQuery.data as any;
                const customerId = resolvedData?.customerPartnerId ?? (installation as any).contractCustomerPartnerId;
                if (customerId != null && resolvedContractId != null) {
                  setLocation(buildContractPath({ customerPartnerId: customerId, contractId: resolvedContractId, tab: 'economia' }));
                }
              }}
              className="min-h-[36px]"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Abrir contrato
            </Button>
          )}
        </CardContent>
      </Card>

      {resolvedContractId != null && (
        <ContractBillingWorkspace contractId={resolvedContractId} />
      )}
    </div>
  );
}
